import { Component, inject, Input, OnInit } from '@angular/core';
import {
  FinancialProductEnum,
  quoteConceptDto,
} from '../../model/standard-quote.model';
import { StandardQuoteService } from '../../standard-quote-service.service';
import { insuranceEnum, type Insurance } from '@app/pages/quote/insurance/insurance-model';

@Component({
  template: ''
})
export class ResultQuoteWrapperComponent implements OnInit {
  private readonly quoteService = inject(StandardQuoteService);
  @Input() financialProduct: string;
  @Input() quoteResult: quoteConceptDto;
  @Input() coverageResult: any;
  /** Deal en edición (p. ej. `form.getRawValue()`). Evita leer siempre `standardQuoteDeals[0]` y desincronizar gracia vs paquete/cálculo. */
  @Input() dealSummary: any;

  quantity: number;

  isLoading: boolean = false;
  insuranceEnum = insuranceEnum;

  ngOnInit() {
    if (this.quoteService.formData)
      this.quantity = Number(this.quoteService.formData?.model.quantity);
  }

  save() {}

  private dealForGraceSummary(): any {
    return (
      this.dealSummary ??
      this.quoteService.formData?.standardQuoteDeals?.[0] ??
      null
    );
  }

  /** Misma prioridad que el body de cálculo: condición dentro del setup financiero, luego la del deal. */
  private resolveGracePeriodMonths(deal: any): number {
    if (!deal) return 0;
    const nested = deal.financialSetup?.financialCondition?.gracePeriod;
    const top = deal.financialCondition?.gracePeriod;
    const raw =
      nested !== undefined && nested !== null ? nested : top ?? 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }

  /** `form.getRawValue()` no incluye estas preferencias (no son controles); vienen del deal persistido o del setup/paquete. */
  private persistedDealRowById(): any {
    const id = this.dealSummary?.id;
    const deals = this.quoteService.formData?.standardQuoteDeals;
    if (id == null || !Array.isArray(deals)) return null;
    return (
      deals.find((d: any) => d?.id == id || String(d?.id) === String(id)) ??
      null
    );
  }

  private pickGraceFlags(
    source: any
  ): { allowCapitalGrace: boolean; allowInterestGrace: boolean } | null {
    if (!source || typeof source !== 'object') return null;
    if (
      !('allowCapitalGrace' in source) &&
      !('allowInterestGrace' in source)
    ) {
      return null;
    }
    const { allowCapitalGrace, allowInterestGrace } = source;
    return {
      allowCapitalGrace: !!allowCapitalGrace,
      allowInterestGrace: !!allowInterestGrace,
    };
  }

  private resolveGracePreferenceFlags(): {
    allowCapitalGrace: boolean;
    allowInterestGrace: boolean;
  } {
    const summary = this.dealSummary;
    const persisted = this.persistedDealRowById();
    const deals = this.quoteService.formData?.standardQuoteDeals;
    const id = summary?.id;
    const first = Array.isArray(deals) ? deals[0] : null;
    const firstSameDeal =
      first != null && id != null &&
      (first.id == id || String(first.id) === String(id))
        ? first
        : null;

    /** API / deal persistido primero: el form suele traer `financialSetup` sin `allowInterestGrace` y ganaba solo "Capital". */
    const sources = [
      persisted,
      persisted?.financialSetup,
      firstSameDeal,
      firstSameDeal?.financialSetup,
      summary,
      summary?.financialSetup,
    ].filter((s) => s != null) as any[];

    let allowCapitalGrace = false;
    let allowInterestGrace = false;
    for (const s of sources) {
      const picked = this.pickGraceFlags(s);
      if (!picked) continue;
      allowCapitalGrace = allowCapitalGrace || picked.allowCapitalGrace;
      allowInterestGrace = allowInterestGrace || picked.allowInterestGrace;
    }
    return { allowCapitalGrace, allowInterestGrace };
  }

  getGracePeriodType() {
    const { allowCapitalGrace, allowInterestGrace } =
      this.resolveGracePreferenceFlags();
    if (allowCapitalGrace && allowInterestGrace) {
      return 'Total';
    }
    if (allowCapitalGrace) {
      return 'Capital';
    }
    if (allowInterestGrace) {
      return 'Intereses';
    }
    return '';
  }

  get gracePeriodAmount() {
    return this.resolveGracePeriodMonths(this.dealForGraceSummary());
  }

  showAmount(insurance: Insurance) {
    if (!insurance) return 0;
    if (!insurance.packageList) return 0;
    return insurance.packageList.amount;
  }

  getCoverageAmountTotal(sumAssured: any){
    const amount = Number(String(sumAssured).replace(/,/g, ''));
    if (isNaN(amount)) {
      const matches = sumAssured.match(/^([\d,]+\.?\d*)(.*)$/);

      if (matches && matches.length >= 3) {
        const numberPart = matches[1];
        const textPart = matches[2];

        const amount = Number(numberPart.replace(/,/g, ''));

        if (!isNaN(amount)) {
          const total = amount * this.quantity;

          return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(total) + textPart;
        }
      }
      return sumAssured;
    }
    const total = amount * this.quantity;
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(total);
  }

  get isBalloonActive(){
    return this.financialProduct != FinancialProductEnum.financialLeasing && this.financialProduct != FinancialProductEnum.pureLease;
  }

  /** Número de mensualidades a las que equivale el depósito en garantía / renta en depósito. */
  get depositRentMonths(): number | null {
    if (!this.quoteResult?.guaranteeDepositAmount || !this.quoteResult?.monthlyPayment || this.quoteResult.monthlyPayment <= 0) {
      return null;
    }
    const months = this.quoteResult.guaranteeDepositAmount / this.quoteResult.monthlyPayment;
    return Math.round(months);
  }

  get amount(){
    return Number(this.coverageResult?.packageList?.amount);
  }
  get insuranceNumber(){
    return Number(this.coverageResult?.packageList?.number);
  }
  get isNumberDifferentCero(){
    const number = this.insuranceNumber;
    return !isNaN(number) && number != 0;
  }
}
